import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  cn,
} from "@exhousing/design-system";
import { ArrowLeft, CheckCircle2, Lock, RotateCw } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { BillingPanel } from "@/components/billing/BillingPanel";
import { ContractActivity } from "@/components/contract/ContractActivity";
import { CommissionCard } from "@/components/contract/CommissionCard";
import { LifecycleStepper } from "@/components/LifecycleStepper";
import { ContractOverview } from "@/components/contract/ContractOverview";
import { CostAdjustmentCard } from "@/components/contract/CostAdjustmentCard";
import { LeasingFeeCard } from "@/components/contract/LeasingFeeCard";
import { ContractTermsCard } from "@/components/contract/ContractTermsCard";
import { ContractGuarantorsCard } from "@/components/contract/ContractGuarantorsCard";
import { CostsCard } from "@/components/contract/CostsCard";
import { DepositCard } from "@/components/contract/DepositCard";
import { DocumentsCard } from "@/components/contract/DocumentsCard";
import { IndexationCard } from "@/components/contract/FinanceCards";
import { SettlementCard } from "@/components/contract/SettlementCard";
import { CorrespondencePanel } from "@/components/inbox/CorrespondencePanel";
import { useMe } from "@/hooks/api/useAuth";
import { isLandlord } from "@/lib/roles";
import {
  useContract,
  useContractLifecycle,
  useRenewContract,
} from "@/hooks/api/useContracts";
import { ContractInspections } from "@/components/contract/ContractInspections";
import { ContractRequirements } from "@/components/contract/ContractRequirements";
import { useContractInvoices } from "@/hooks/api/useInvoices";
import { useInspections } from "@/hooks/api/useInspections";
import { useRequirements } from "@/hooks/api/useRequirements";
import { useProperty } from "@/hooks/api/useProperties";
import { buildMilestones } from "@/lib/contractMilestones";
import { money } from "@/lib/money";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "terms", label: "Terms" },
  { id: "billing", label: "Billing" },
  { id: "deposit", label: "Deposit" },
  { id: "inspections", label: "Inspections" },
  { id: "documents", label: "Documents" },
  { id: "activity", label: "Activity" },
] as const;

type ContractTab = (typeof TABS)[number]["id"];

// The landlord follows the agreement, not the agency's operation: no billing,
// deposit ops, inspections, requirement management or correspondence.
const LANDLORD_TABS: readonly ContractTab[] = ["overview", "terms"];

function euro(v: number | null | undefined): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(v);
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-medium tabular-nums">{value}</p>
    </div>
  );
}

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: contract, isLoading, isError } = useContract(id);
  const { data: me } = useMe();
  const landlordView = isLandlord(me);
  const { data: property } = useProperty(contract?.property_id);
  // Landlords lack inspection permissions — skip the query, stepper degrades.
  const { data: inspections } = useInspections(id, { enabled: !landlordView });
  const { data: requirements } = useRequirements(id);
  const lifecycle = useContractLifecycle(id ?? "");
  const renew = useRenewContract(id ?? "");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [endOpen, setEndOpen] = useState(false);
  const [endDate, setEndDate] = useState("");
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewStart, setRenewStart] = useState("");
  const [renewEnd, setRenewEnd] = useState("");
  const [renewRent, setRenewRent] = useState("");
  // Single pending confirmation for irreversible lifecycle actions.
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    tone: "default" | "destructive";
    run: () => void;
  } | null>(null);

  const tab = (searchParams.get("tab") as ContractTab) ?? "overview";
  const setTab = (t: ContractTab) => setSearchParams({ tab: t });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (isError || !contract)
    return <p className="text-sm text-danger">Contract not found.</p>;

  const status = contract.status;

  // Pre-flight for the Activate gate: the backend requires a signed lease, so don't offer
  // a button that will just bounce. Signed = an attested signed date or the accepted
  // "sign / activate" lease requirement.
  const leaseSigned =
    !!contract.signed_date ||
    (requirements ?? []).some(
      (r) => r.mode === "sign" && r.gate === "activate" && r.status === "accepted",
    );

  // A lease's natural name is its address + tenant, not a UUID. Fall back to the
  // reference code, then a short id.
  const addressLine = property
    ? [
        property.street,
        [property.house_number, property.house_number_suffix].filter(Boolean).join(""),
      ]
        .filter(Boolean)
        .join(" ")
    : "";
  const tenantNames = contract.tenants.map((t) => t.display_name).join(", ");
  const title = addressLine || contract.reference || `Contract ${contract.id.slice(0, 8)}`;

  return (
    <div className="space-y-6">
      <Link
        to="/contracts"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Contracts
      </Link>

      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">
            {title}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <StatusBadge status={status} />
            <span className="capitalize">{contract.term} term</span>
            {tenantNames && (
              <>
                <span>·</span>
                <span>{tenantNames}</span>
              </>
            )}
            {property?.city && (
              <>
                <span>·</span>
                <span>{property.city}</span>
              </>
            )}
          </p>
          {contract.reference && addressLine && (
            <p className="mt-0.5 text-xs text-muted-foreground">Ref {contract.reference}</p>
          )}
          {contract.predecessor_id && (
            <Link
              to={`/contracts/${contract.predecessor_id}`}
              className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCw className="size-3" />
              Renewal of an earlier term
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!landlordView && status === "draft" && (
            <>
              <Button
                size="sm"
                variant="ghost"
                disabled={lifecycle.cancel.isPending}
                onClick={() =>
                  setConfirm({
                    title: "Cancel this draft contract?",
                    message: "The draft will be discarded. This can't be undone.",
                    confirmLabel: "Cancel contract",
                    tone: "destructive",
                    run: () =>
                      lifecycle.cancel.mutate(undefined, {
                        onSuccess: () => toast.success("Contract cancelled"),
                        onError: (e) => toast.error(e.message),
                      }),
                  })
                }
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="accent"
                disabled={lifecycle.activate.isPending || !leaseSigned}
                title={
                  leaseSigned
                    ? undefined
                    : "Sign the lease (Documents tab) or set the signed date (Terms tab) first"
                }
                onClick={() =>
                  lifecycle.activate.mutate(undefined, {
                    onSuccess: () => toast.success("Activated — terms are now locked"),
                    onError: (e) => toast.error(e.message),
                  })
                }
              >
                <CheckCircle2 className="size-4" />
                Activate
              </Button>
            </>
          )}
          {!landlordView && (status === "active" || status === "ending") && (
            <Button size="sm" variant="outline" onClick={() => setRenewOpen((v) => !v)}>
              <RotateCw className="size-4" />
              Renew
            </Button>
          )}
          {!landlordView && status === "active" && (
            <Button size="sm" variant="outline" onClick={() => setEndOpen((v) => !v)}>
              End contract
            </Button>
          )}
          {!landlordView && status === "ending" && (
            <Button
              size="sm"
              variant="accent"
              disabled={lifecycle.close.isPending}
              onClick={() =>
                setConfirm({
                  title: "Close this contract?",
                  message: "The contract becomes read-only. This can't be undone.",
                  confirmLabel: "Close contract",
                  tone: "destructive",
                  run: () =>
                    lifecycle.close.mutate(undefined, {
                      onSuccess: () => toast.success("Contract closed"),
                      onError: (e) => toast.error(e.message),
                    }),
                })
              }
            >
              Close
            </Button>
          )}
        </div>
      </header>

      {/* Lifecycle progress — states + check-in/out milestones, steps anchor to tabs */}
      <LifecycleStepper
        {...buildMilestones(status, inspections ?? [], requirements ?? [])}
        onNavigate={(t) => setTab(t as ContractTab)}
      />

      {/* End-date prompt */}
      {endOpen && status === "active" && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">End date</p>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-9 w-44"
            />
          </div>
          <Button
            size="sm"
            disabled={!endDate || lifecycle.end.isPending}
            onClick={() =>
              lifecycle.end.mutate(endDate, {
                onSuccess: () => {
                  toast.success("Wind-down started");
                  setEndOpen(false);
                },
                onError: (e) => toast.error(e.message),
              })
            }
          >
            Confirm end date
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEndOpen(false)}>
            Cancel
          </Button>
        </div>
      )}

      {/* Renewal prompt */}
      {renewOpen && (status === "active" || status === "ending") && (
        <div className="space-y-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
          <p className="text-sm text-muted-foreground">
            Create the next term as a new draft (pricing carries over; edit before
            activating). Activating it closes this one.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">New start date</p>
              <Input
                type="date"
                value={renewStart}
                onChange={(e) => setRenewStart(e.target.value)}
                className="h-9 w-44"
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">New end date (optional)</p>
              <Input
                type="date"
                value={renewEnd}
                onChange={(e) => setRenewEnd(e.target.value)}
                className="h-9 w-44"
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">New rent (optional)</p>
              <Input
                type="number"
                placeholder={contract.rental_price?.toString() ?? "€"}
                value={renewRent}
                onChange={(e) => setRenewRent(e.target.value)}
                className="h-9 w-32"
              />
            </div>
            <Button
              size="sm"
              disabled={!renewStart || renew.isPending}
              onClick={() =>
                renew.mutate(
                  {
                    start_date: renewStart,
                    end_date: renewEnd || null,
                    rental_price: renewRent ? Number(renewRent) : null,
                    copy_tenants: true,
                  },
                  {
                    onSuccess: (created) => {
                      toast.success("Renewal draft created");
                      setRenewOpen(false);
                      navigate(`/contracts/${created.id}`);
                    },
                    onError: (e) => toast.error(e.message),
                  },
                )
              }
            >
              {renew.isPending ? "Creating…" : "Create renewal"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRenewOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.filter((t) => !landlordView || LANDLORD_TABS.includes(t.id)).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              tab === t.id
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "overview" && <ContractOverview contract={contract} onGoToTab={setTab} />}

      {tab === "terms" && (
        <div className="space-y-6">
          {status !== "draft" && (
            <div className="flex items-center gap-3 rounded-lg bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
              <Lock className="size-4 shrink-0" />
              Signed terms are locked. Rent changes through indexation, service costs
              through a cost adjustment (Billing tab), and the end date by ending the
              contract.
            </div>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-5 sm:grid-cols-4">
              <Stat label="Rent / mo" value={euro(contract.rental_price)} />
              <Stat label="Service costs" value={euro(contract.service_costs)} />
              <Stat label="Deposit" value={euro(contract.deposit_amount)} />
              <Stat label="Payment day" value={contract.payment_day?.toString() ?? "—"} />
              <Stat label="Start" value={contract.start_date ?? "—"} />
              <Stat label="End" value={contract.end_date ?? "—"} />
              <Stat label="Signed" value={contract.signed_date ?? "—"} />
              <Stat label="Indexation" value={contract.rent_indexation_date ?? "—"} />
            </CardContent>
          </Card>
          <CostsCard contract={contract} />
          {!landlordView && <CommissionCard contract={contract} />}
          <ContractTermsCard contract={contract} />
          {!landlordView && <ContractGuarantorsCard contract={contract} />}
          {!landlordView && <IndexationCard contractId={contract.id} />}
        </div>
      )}

      {tab === "billing" && (
        <div className="space-y-6">
          <BillingPanel contractId={contract.id} />
          {(status === "active" || status === "ending") && (
            <>
              <CostAdjustmentCard contract={contract} />
              <SettlementCard contractId={contract.id} />
              <LeasingFeeCard contract={contract} />
            </>
          )}
          <ContractInvoices contractId={contract.id} />
        </div>
      )}

      {tab === "deposit" && <DepositCard contractId={contract.id} />}

      {tab === "inspections" && <ContractInspections contractId={contract.id} />}

      {tab === "documents" && (
        <div className="space-y-8">
          <ContractRequirements contractId={contract.id} />
          <DocumentsCard contract={contract} />
        </div>
      )}

      {tab === "activity" && (
        <div className="space-y-6">
          <ContractActivity contract={contract} />
          <CorrespondencePanel entityType="contract" entityId={contract.id} />
        </div>
      )}

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.title ?? ""}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        tone={confirm?.tone}
        onConfirm={() => {
          confirm?.run();
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

function ContractInvoices({ contractId }: { contractId: string }) {
  const { data: invoices, isLoading } = useContractInvoices(contractId);
  if (isLoading) return null;
  const rows = invoices ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoices</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No invoices yet.</p>
        ) : (
          <ul className="divide-y divide-border/60 text-sm">
            {rows.map((i) => (
              <li key={i.id} className="flex items-center gap-3 py-2.5">
                <Link
                  to={`/invoices/${i.id}`}
                  className="font-medium tabular-nums hover:text-primary"
                >
                  {i.number ?? "Draft"}
                </Link>
                <StatusBadge status={i.status} />
                <span className="ml-auto tabular-nums">{money(i.total)}</span>
                <span className="w-24 text-right text-xs text-muted-foreground tabular-nums">
                  {i.due_date ? `due ${i.due_date}` : "—"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
